<template>
  <v-card variant="outlined" class="tp-panel">
    <div class="pa-3 pb-0">
      <div class="text-subtitle-2">入力支援</div>
      <div class="text-caption text-grey-darken-1">
        {{ selectedCell ? selectedCell.name : "セルを選択してください" }}
      </div>
    </div>

    <!--
      パネルは1440px想定でも幅が狭い (lg=3)。既定のタブ幅だと3つ入りきらず
      左右の矢印でスクロールさせられるため、詰めて必ず収まるようにする。
    -->
    <v-tabs v-model="tab" color="primary" density="compact" grow class="mt-2 tp-panel__tabs">
      <v-tab value="comment">
        コメント
        <v-chip v-if="unresolvedTotal > 0" class="ml-1" size="x-small" variant="flat" color="warning">
          {{ unresolvedTotal }}
        </v-chip>
      </v-tab>
      <v-tab value="phrase">文例</v-tab>
      <v-tab value="past">過去参照</v-tab>
    </v-tabs>

    <v-divider></v-divider>

    <div class="tp-panel__body pa-3">
      <!-- コメント -->
      <template v-if="tab === 'comment'">
        <div v-if="!selectedItemKey" class="text-caption text-grey">
          コメントを見るセルを選択してください。
        </div>
        <template v-else>
          <div v-if="threads.length === 0" class="text-caption text-grey mb-3">
            このセルにはまだコメントがありません。
          </div>

          <div v-for="thread in threads" :key="thread.commentId" class="tp-thread mb-3">
            <div class="d-flex align-center flex-wrap ga-1">
              <span class="text-caption font-weight-bold tp-thread__author">
                {{ thread.authorName }}
              </span>
              <span class="text-caption text-grey-darken-1 tp-thread__at">
                {{ formatDateTime(thread.createdAt) }}
              </span>
              <v-spacer />
              <v-chip
                :color="thread.isResolved ? 'success' : 'warning'"
                size="x-small"
                variant="flat"
              >
                {{ thread.isResolved ? "解決済み" : "未解決" }}
              </v-chip>
            </div>
            <div class="text-body-2 mt-1 tp-thread__body">{{ thread.body }}</div>

            <div v-for="reply in thread.replies" :key="reply.commentId" class="tp-reply mt-2">
              <div class="d-flex align-center">
                <span class="text-caption font-weight-bold">{{ reply.authorName }}</span>
                <span class="text-caption text-grey-darken-1 ml-2">
                  {{ formatDateTime(reply.createdAt) }}
                </span>
              </div>
              <div class="text-body-2 tp-thread__body">{{ reply.body }}</div>
            </div>

            <div class="mt-2 d-flex align-center ga-2">
              <v-btn
                size="x-small"
                variant="text"
                color="primary"
                @click="replyTo = replyTo === thread.commentId ? null : thread.commentId"
              >
                返信
              </v-btn>
              <v-btn
                size="x-small"
                variant="text"
                :color="thread.isResolved ? 'grey-darken-1' : 'success'"
                :loading="resolvingCommentId === thread.commentId"
                @click="toggleResolve(thread)"
              >
                {{ thread.isResolved ? "未解決に戻す" : "解決にする" }}
              </v-btn>
              <span v-if="thread.isResolved && thread.resolvedByName" class="text-caption text-grey">
                {{ thread.resolvedByName }}が解決
              </span>
            </div>

            <div v-if="replyTo === thread.commentId" class="mt-2">
              <v-textarea
                v-model="replyBody"
                rows="2"
                variant="outlined"
                density="compact"
                hide-details
                color="primary"
                placeholder="返信を入力"
                :counter="commentBodyMaxLength"
              ></v-textarea>
              <div class="d-flex justify-end mt-1">
                <v-btn size="small" variant="text" @click="cancelReply">キャンセル</v-btn>
                <v-btn
                  size="small"
                  color="primary"
                  variant="flat"
                  :disabled="!isValidBody(replyBody)"
                  :loading="posting"
                  @click="postComment(thread.commentId)"
                >
                  送信
                </v-btn>
              </div>
            </div>
          </div>

          <v-divider class="my-3"></v-divider>

          <v-textarea
            v-model="newBody"
            rows="3"
            variant="outlined"
            density="compact"
            hide-details
            color="primary"
            placeholder="このセルへのコメントを入力"
            :counter="commentBodyMaxLength"
          ></v-textarea>
          <div class="d-flex justify-end mt-2">
            <v-btn
              size="small"
              color="primary"
              variant="flat"
              :disabled="!isValidBody(newBody)"
              :loading="posting"
              @click="postComment(null)"
            >
              コメントする
            </v-btn>
          </div>
        </template>

        <template v-if="otherUnresolvedItems.length > 0">
          <v-divider class="my-3"></v-divider>
          <div class="text-caption text-grey-darken-1 mb-1">
            他に未解決{{ otherUnresolvedItems.length }}項目
          </div>
          <v-chip
            v-for="item in otherUnresolvedItems"
            :key="item.itemKey"
            class="mr-1 mb-1"
            size="small"
            variant="outlined"
            color="warning"
            @click="$emit('jump-item', item.itemKey)"
          >
            {{ item.name }}（{{ item.count }}）
          </v-chip>
        </template>
      </template>

      <!-- 文例 -->
      <template v-else-if="tab === 'phrase'">
        <div v-if="!selectedCell || !selectedCell.phraseKey" class="text-caption text-grey">
          このセルには文例グループが設定されていません。帳票設定で設定できます。
        </div>
        <template v-else>
          <div class="text-caption text-grey-darken-1 mb-2">
            文例グループ: {{ selectedCell.phraseKey }}
          </div>
          <v-text-field
            v-model="phraseKeyword"
            variant="outlined"
            density="compact"
            hide-details
            clearable
            color="primary"
            placeholder="文例を検索"
            prepend-inner-icon="mdi-magnify"
            @keyup.enter="loadPhrases"
            @click:clear="clearPhraseKeyword"
          ></v-text-field>
          <div v-if="loadingPhrases" class="text-caption text-grey mt-3">読み込み中...</div>
          <div v-else-if="phrases.length === 0" class="text-caption text-grey mt-3">
            登録された文例がありません。
          </div>
          <div v-for="phrase in phrases" :key="phrase.phraseId" class="tp-suggest mt-2">
            <div class="text-body-2 tp-thread__body">{{ phrase.body }}</div>
            <div class="d-flex justify-end">
              <v-btn
                size="x-small"
                variant="text"
                color="primary"
                :disabled="!editable"
                @click="$emit('insert-text', phrase.body)"
              >
                本文に挿入
              </v-btn>
            </div>
          </div>
        </template>
      </template>

      <!-- 過去参照 -->
      <template v-else>
        <div v-if="!selectedItemKey" class="text-caption text-grey">
          セルを選択してください。
        </div>
        <template v-else>
          <div v-if="loadingPastValues" class="text-caption text-grey">読み込み中...</div>
          <div v-else-if="pastValues.length === 0" class="text-caption text-grey">
            過去の帳票に同じ項目の記入がありません。
          </div>
          <div v-for="(past, index) in pastValues" :key="`${past.planId}-${index}`" class="tp-suggest mt-2">
            <div class="text-caption text-grey-darken-1">
              {{ past.planLabel }}
            </div>
            <div class="text-body-2 tp-thread__body mt-1">{{ past.value }}</div>
            <div class="d-flex justify-end ga-1">
              <v-btn size="x-small" variant="text" @click="$emit('jump-plan', past.planId)">
                帳票を開く
              </v-btn>
              <v-btn
                size="x-small"
                variant="text"
                color="primary"
                :disabled="!editable"
                @click="$emit('insert-text', past.value)"
              >
                本文に挿入
              </v-btn>
            </div>
          </div>
        </template>
      </template>
    </div>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import {
  NurseryTeachingPlanComment,
  NurseryTeachingPlanLayout,
  NurseryTeachingPlanLayoutCell,
  NurseryTeachingPlanPastValueItem,
  NurseryTeachingPlanPhraseItem,
} from "chaild-api/lib";
import ApiTeachingPlan from "@/api/ApiTeachingPlan";
import TeachingPlanUtil from "@/utils/TeachingPlanUtil";
import { commentBodyMaxLength } from "@/constants/teachingPlan";

/**
 * エディタ右側の入力支援パネル (コメント / 文例 / 過去参照)。
 * コメントは親が保持する一覧を受け取り、更新後の一覧を emit で返す。
 */
export default defineComponent({
  name: "TeachingPlanSupportPanel",

  props: {
    nurseryId: {
      type: Number,
      required: true,
    },
    planId: {
      type: String,
      required: true,
    },
    templateId: {
      type: [String, null] as PropType<string | null>,
      default: null,
    },
    layout: {
      type: [Object, null] as PropType<NurseryTeachingPlanLayout | null>,
      default: null,
    },
    selectedItemKey: {
      type: [String, null] as PropType<string | null>,
      default: null,
    },
    comments: {
      type: Array as PropType<NurseryTeachingPlanComment[]>,
      default: () => [],
    },
    editable: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["insert-text", "jump-plan", "jump-item", "comments-updated"],

  data() {
    return {
      tab: "comment" as "comment" | "phrase" | "past",
      newBody: "",
      replyBody: "",
      replyTo: null as string | null,
      posting: false,
      resolvingCommentId: null as string | null,
      phraseKeyword: "",
      phrases: [] as NurseryTeachingPlanPhraseItem[],
      loadingPhrases: false,
      pastValues: [] as NurseryTeachingPlanPastValueItem[],
      loadingPastValues: false,
      /** 文例・過去参照の後着応答を捨てるためのトークン */
      suggestToken: 0,
      commentBodyMaxLength,
    };
  },

  computed: {
    selectedCell(): NurseryTeachingPlanLayoutCell | null {
      if (!this.layout || !this.selectedItemKey) {
        return null;
      }
      for (const row of this.layout.rows) {
        for (const cell of row.cells) {
          if (cell.itemKey === this.selectedItemKey) {
            return cell;
          }
        }
      }
      return null;
    },
    threads(): NurseryTeachingPlanComment[] {
      return this.comments.filter((comment) => comment.itemKey === this.selectedItemKey);
    },
    unresolvedTotal(): number {
      return this.comments.filter((comment) => !comment.isResolved).length;
    },
    /** 選択中セル以外で未解決コメントを持つ項目 */
    otherUnresolvedItems(): { itemKey: string; name: string; count: number }[] {
      const counts = new Map<string, number>();
      for (const comment of this.comments) {
        if (comment.isResolved || comment.itemKey === this.selectedItemKey) {
          continue;
        }
        counts.set(comment.itemKey, (counts.get(comment.itemKey) || 0) + 1);
      }
      return Array.from(counts.entries()).map(([itemKey, count]) => ({
        itemKey,
        name: this.cellName(itemKey),
        count,
      }));
    },
  },

  watch: {
    selectedItemKey() {
      this.resetSuggestions();
    },
    // 同じ itemKey のまま帳票やレイアウトが変わることがあるため、こちらも監視する
    // (放置すると旧帳票の過去値・旧レイアウトの文例が残る)
    planId() {
      this.resetSuggestions();
    },
    templateId() {
      this.resetSuggestions();
    },
    tab() {
      this.loadForTab();
    },
  },

  methods: {
    formatDateTime(value: string): string {
      return TeachingPlanUtil.formatDateTime(value);
    },
    isValidBody(body: string): boolean {
      const trimmed = (body || "").trim();
      return trimmed.length > 0 && trimmed.length <= commentBodyMaxLength;
    },
    cellName(itemKey: string): string {
      if (!this.layout) {
        return itemKey;
      }
      for (const row of this.layout.rows) {
        for (const cell of row.cells) {
          if (cell.itemKey === itemKey) {
            return cell.name;
          }
        }
      }
      return itemKey;
    },
    cancelReply() {
      this.replyTo = null;
      this.replyBody = "";
    },
    /** 選択対象が変わったら入力途中と取得済みの候補を捨てて引き直す */
    resetSuggestions() {
      this.suggestToken += 1;
      this.replyTo = null;
      this.replyBody = "";
      this.newBody = "";
      this.phraseKeyword = "";
      this.phrases = [];
      this.pastValues = [];
      this.loadingPhrases = false;
      this.loadingPastValues = false;
      this.loadForTab();
    },
    clearPhraseKeyword() {
      this.phraseKeyword = "";
      this.loadPhrases();
    },
    loadForTab() {
      if (this.tab === "phrase") {
        this.loadPhrases();
      } else if (this.tab === "past") {
        this.loadPastValues();
      }
    },
    /**
     * セルを切り替えながら読み込むと応答が前後しうる。
     * 「本文に挿入」は親の現在の選択セルへ書き込むため、古い応答をそのまま出すと
     * 別のセルの内容を挿入してしまう。リクエスト時の条件と一致した応答だけ反映する。
     */
    async loadPhrases() {
      const templateId = this.templateId;
      const phraseKey = this.selectedCell ? this.selectedCell.phraseKey : null;
      const itemKey = this.selectedItemKey;
      if (!templateId || !phraseKey) {
        this.phrases = [];
        return;
      }
      this.suggestToken += 1;
      const token = this.suggestToken;
      const keyword = this.phraseKeyword || undefined;
      this.loadingPhrases = true;
      const response = await ApiTeachingPlan.listCellPhrases({
        nurseryId: this.nurseryId,
        templateId,
        phraseKey,
        keyword,
      }).catch(() => null);
      // 別レイアウトに同名の itemKey があるため、templateId と phraseKey も照合する
      if (
        token !== this.suggestToken ||
        this.selectedItemKey !== itemKey ||
        this.templateId !== templateId ||
        !this.selectedCell ||
        this.selectedCell.phraseKey !== phraseKey
      ) {
        return;
      }
      this.loadingPhrases = false;
      this.phrases = response ? response.data : [];
    },
    async loadPastValues() {
      const itemKey = this.selectedItemKey;
      const planId = this.planId;
      if (!itemKey) {
        this.pastValues = [];
        return;
      }
      this.suggestToken += 1;
      const token = this.suggestToken;
      this.loadingPastValues = true;
      const response = await ApiTeachingPlan.listPastValues({
        nurseryId: this.nurseryId,
        planId,
        itemKey,
        limit: 10,
      }).catch(() => null);
      if (token !== this.suggestToken || this.selectedItemKey !== itemKey || this.planId !== planId) {
        return;
      }
      this.loadingPastValues = false;
      this.pastValues = response ? response.data : [];
    },
    async postComment(parentCommentId: string | null) {
      if (!this.selectedItemKey) {
        return;
      }
      const body = parentCommentId ? this.replyBody : this.newBody;
      if (!this.isValidBody(body)) {
        return;
      }
      const planId = this.planId;
      this.posting = true;
      const response = await ApiTeachingPlan.createComment({
        planId,
        itemKey: this.selectedItemKey,
        body: body.trim(),
        parentCommentId: parentCommentId || undefined,
      }).catch(() => null);
      this.posting = false;
      // 参照元ジャンプで帳票が切り替わった後に旧帳票の応答が届くと、
      // comments-updated 経由で別帳票のコメント一覧を上書きしてしまうため世代を照合する
      if (this.planId !== planId) {
        return;
      }
      if (!response) {
        return;
      }
      if (parentCommentId) {
        this.cancelReply();
      } else {
        this.newBody = "";
      }
      this.$emit("comments-updated", response.data);
    },
    async toggleResolve(thread: NurseryTeachingPlanComment) {
      const planId = this.planId;
      this.resolvingCommentId = thread.commentId;
      const response = await ApiTeachingPlan.resolveComment({
        planId,
        commentId: thread.commentId,
        action: thread.isResolved ? "reopen" : "resolve",
      }).catch(() => null);
      this.resolvingCommentId = null;
      // postComment と同じ理由で世代を照合する
      if (this.planId !== planId) {
        return;
      }
      if (response) {
        this.$emit("comments-updated", response.data);
      }
    },
  },
});
</script>

<style scoped>
.tp-panel {
  display: flex;
  flex-direction: column;
  /* 画面に収まる範囲で中身なりに伸ばす (親の高さに引き伸ばさない) */
  max-height: calc(100vh - 24px);
}
.tp-panel__body {
  flex: 1;
  overflow-y: auto;
}
/* 狭いパネルでも3タブが1行に収まるようにする (矢印スクロールを出さない) */
.tp-panel__tabs :deep(.v-tab) {
  min-width: 0;
  padding: 0 6px;
  font-size: 12px;
  letter-spacing: 0;
}
/* 狭いパネルで氏名が途中で折れないようにする */
.tp-thread__author,
.tp-thread__at {
  white-space: nowrap;
}
.tp-thread {
  border-left: 3px solid #ffcc80;
  padding-left: 8px;
}
.tp-thread__body {
  white-space: pre-wrap;
  word-break: break-word;
}
.tp-reply {
  border-left: 2px solid #eceff1;
  padding-left: 8px;
}
.tp-suggest {
  border: 1px solid #eceff1;
  border-radius: 4px;
  padding: 8px;
}
</style>
